import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, HelpCircle, Search } from 'lucide-react';

const faqCategories = [
  {
    category: 'General',
    faqs: [
      {
        question: 'What services does Felix Infotech provide?',
        answer: 'Felix Infotech provides comprehensive ICT solutions including servers & storage, networking, workstations, cloud & data centre services, software solutions, and cyber security services. We serve businesses of all sizes across India and the Middle East.'
      },
      {
        question: 'How long has Felix Infotech been in business?',
        answer: 'Felix Infotech has been in business for over 18 years, serving 700+ clients with a team of 45+ expert employees. We have offices in Chennai, Cochin, Bangalore, Hyderabad, Coimbatore, and Dubai.'
      },
      {
        question: 'What industries do you serve?',
        answer: 'We serve various industries including BFSI, Healthcare, Education, Manufacturing, Retail, Government, and IT/ITES sectors. Our solutions are customized to meet the specific needs of each industry.'
      },
    ]
  },
  {
    category: 'ICT Solutions',
    faqs: [
      {
        question: 'What brands do you work with for hardware?',
        answer: 'We are authorized partners for all major brands including HP, Dell, Lenovo, Cisco, Juniper, Aruba, NetApp, Huawei, and many more. Our vendor-agnostic approach means we recommend the best solution for your needs.'
      },
      {
        question: 'Do you provide installation and support?',
        answer: 'Yes, we provide end-to-end services including site survey, installation, configuration, testing, commissioning, and ongoing support. We offer AMC (Annual Maintenance Contracts) with 24/7 support and 4-hour response time.'
      },
      {
        question: 'Do you offer rental solutions?',
        answer: 'Yes, we offer flexible IT equipment rental for desktops, laptops, servers, and networking equipment. This is ideal for events, projects, temporary staff, or testing before buying.'
      },
    ]
  },
  {
    category: 'Cloud Services',
    faqs: [
      {
        question: 'What cloud services do you offer?',
        answer: 'We offer public cloud (AWS, Azure, GCP), private cloud, hybrid cloud, cloud migration, VDI solutions, disaster recovery, and managed cloud services. We help you choose the right cloud strategy for your business.'
      },
      {
        question: 'Can you help migrate our existing infrastructure to cloud?',
        answer: 'Yes, we provide complete cloud migration services including assessment, planning, migration execution, and post-migration support. We ensure minimal downtime and data integrity throughout the process.'
      },
      {
        question: 'What is your uptime guarantee?',
        answer: 'We offer 99.99% uptime SLA for our managed services. Our data centers and cloud infrastructure are designed with redundancy to ensure high availability.'
      },
    ]
  },
  {
    category: 'Cyber Security',
    faqs: [
      {
        question: 'What security assessments do you perform?',
        answer: 'We perform comprehensive security assessments including Vulnerability Assessment & Penetration Testing (VAPT), network security audits, application security testing, compliance audits, and red team assessments.'
      },
      {
        question: 'Do you offer 24/7 security monitoring?',
        answer: 'Yes, our Security Operations Center (SOC) provides 24/7 monitoring, threat detection, incident response, and security analytics. We use advanced SIEM tools and threat intelligence.'
      },
      {
        question: 'Can you help with compliance requirements?',
        answer: 'Yes, we help organizations achieve compliance with various standards including ISO 27001, PCI-DSS, HIPAA, GDPR, and SOC 2. We provide gap analysis, implementation, and audit support.'
      },
    ]
  },
  {
    category: 'Support & Pricing',
    faqs: [
      {
        question: 'How can I get a quote for services?',
        answer: 'You can request a quote by filling out our contact form, calling us at 044 42613839, or emailing info@felixinfotech.com. Our team will assess your requirements and provide a customized proposal.'
      },
      {
        question: 'What are your support hours?',
        answer: 'Our standard support hours are Monday to Saturday, 9 AM to 6 PM. For customers with AMC or managed services contracts, we provide 24/7 support with dedicated response times.'
      },
      {
        question: 'Do you offer financing options?',
        answer: 'Yes, we offer flexible payment terms and can arrange financing through our banking partners for large projects. We also offer OPEX models for certain services.'
      },
    ]
  },
];

const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-gray-200">
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between py-5 text-left group"
    >
      <span className="font-medium text-gray-900 group-hover:text-[#0066CC] transition-colors pr-8">
        {faq.question}
      </span>
      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-5 text-gray-600 text-sm leading-relaxed">
        {faq.answer}
      </p>
    </motion.div>
  </div>
);

export const FAQPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeCategory, setActiveCategory] = useState('General');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredFaqs = searchQuery
    ? faqCategories.flatMap(cat => 
        cat.faqs.filter(faq => 
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : faqCategories.find(cat => cat.category === activeCategory)?.faqs || [];

  return (
    <>
      <Helmet>
        <title>FAQs - Felix Infotech | Frequently Asked Questions about IT Services</title>
        <meta name="description" content="Find answers to frequently asked questions about Felix Infotech's IT services, cloud solutions, cyber security, pricing, and support. Get expert guidance." />
        <meta name="keywords" content="Felix Infotech FAQ, IT solutions FAQ, cloud services questions, cybersecurity FAQ, managed services questions, IT support queries" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="FAQs - Felix Infotech | IT Services Questions Answered" />
        <meta property="og:description" content="Answers to common questions about IT services, cloud, cybersecurity, and support." />
        <meta property="og:url" content="https://felixinfotech.com/faq" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Felix Infotech FAQs" />
        <meta name="twitter:description" content="Common questions about our IT services, cloud, and cybersecurity solutions." />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What services does Felix Infotech provide?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Felix Infotech provides comprehensive ICT solutions including servers & storage, networking, workstations, cloud & data centre services, software solutions, and cyber security services."
                }
              },
              {
                "@type": "Question",
                "name": "How long has Felix Infotech been in business?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Felix Infotech has been in business for over 22 years, serving 100+ clients with a team of 100+ expert employees across India, Dubai, and Singapore."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer 24/7 security monitoring?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, our Security Operations Center (SOC) provides 24/7 monitoring, threat detection, incident response, and security analytics using advanced SIEM tools and threat intelligence."
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title="Frequently Asked Questions"
          subtitle="How Can We Help?"
          description="Find answers to common questions about our services, support, and solutions."
          badge="FAQs"
          animationType="ict"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'FAQs' }]}
        />

        <section className="py-16">
          <div className="container mx-auto px-6">
            {/* Search */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-[#0066CC] focus:ring-2 focus:ring-[#0066CC]/20 outline-none transition-all"
                />
              </div>
            </div>

            {/* Category Tabs */}
            {!searchQuery && (
              <div className="flex flex-wrap justify-center gap-2 mb-12">
                {faqCategories.map((cat) => (
                  <button
                    key={cat.category}
                    onClick={() => {
                      setActiveCategory(cat.category);
                      setOpenFAQ(null);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeCategory === cat.category
                        ? 'bg-[#0066CC] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat.category}
                  </button>
                ))}
              </div>
            )}

            {/* FAQs */}
            <div className="max-w-3xl mx-auto">
              {searchQuery && (
                <p className="text-sm text-gray-500 mb-6">
                  Found {filteredFaqs.length} result(s) for "{searchQuery}"
                </p>
              )}
              
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((faq, index) => (
                  <FAQItem
                    key={index}
                    faq={faq}
                    isOpen={openFAQ === index}
                    toggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                  />
                ))
              ) : (
                <div className="text-center py-12">
                  <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No FAQs found. Try a different search term.</p>
                </div>
              )}
            </div>

            {/* Still have questions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-16 p-8 bg-gray-50 rounded-2xl max-w-2xl mx-auto"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Still have questions?</h3>
              <p className="text-gray-600 mb-4">Can't find what you're looking for? Our team is here to help.</p>
              <a href="/contact" className="inline-flex items-center gap-2 text-[#0066CC] font-medium hover:underline">
                Contact Us <span>→</span>
              </a>
            </motion.div>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default FAQPage;
